/**
 * The narration layer, mounted once at the app root.
 *
 *  1. Caption bar — a bottom-center pill that fades `caption` (from context)
 *     in/out. The cinematic ("Simulează o comandă") drives it via setCaption().
 *  2. Guided-tour overlay — a spotlight + tooltip scaffold driven by the tour
 *     state in context (tourSteps / tourIndex / next/prev/stop). The tour agent
 *     only needs to provide `TourStep[]` (with optional `anchor` matching a
 *     `data-tour="…"` attribute on any element) via `startTour(steps)`.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeft, ArrowRight, Sparkles, X } from 'lucide-react'
import { type CSSProperties, useEffect, useLayoutEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { useApp } from '@/context'

// ---------------------------------------------------------------------------
//  Caption bar (cinematic narration)
// ---------------------------------------------------------------------------
function CaptionBar() {
  const { caption } = useApp()
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4">
      <AnimatePresence mode="wait">
        {caption && (
          <motion.div
            key={caption}
            initial={{ opacity: 0, y: 16, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto flex max-w-xl items-center gap-3 rounded-2xl border border-border bg-card/95 px-5 py-3 shadow-paper-lg backdrop-blur"
          >
            <span className="relative flex size-2.5 shrink-0">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber opacity-70" />
              <span className="relative inline-flex size-2.5 rounded-full bg-amber" />
            </span>
            <p className="text-sm leading-snug text-foreground">{caption}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Guided-tour overlay
// ---------------------------------------------------------------------------
interface Rect {
  top: number
  left: number
  width: number
  height: number
}

function useAnchorRect(anchor: string | undefined, active: boolean, dep: number): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null)

  useLayoutEffect(() => {
    if (!active || !anchor) {
      setRect(null)
      return
    }
    let raf = 0
    const measure = () => {
      const el = document.querySelector(`[data-tour="${anchor}"]`)
      if (el) {
        const r = el.getBoundingClientRect()
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      } else {
        setRect(null)
      }
    }
    // let the target screen render first
    raf = requestAnimationFrame(() => {
      measure()
      // one more pass after transitions settle
      setTimeout(measure, 180)
    })
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [anchor, active, dep])

  return rect
}

function TourOverlay() {
  const { tourActive, tourSteps, tourIndex, setActiveTab, nextTour, prevTour, stopTour } = useApp()
  const step = tourSteps[tourIndex]

  // move to the step's tab whenever the step changes
  useEffect(() => {
    if (tourActive && step) setActiveTab(step.tab)
  }, [tourActive, step, setActiveTab])

  // keyboard controls
  useEffect(() => {
    if (!tourActive) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopTour()
      else if (e.key === 'ArrowRight') nextTour()
      else if (e.key === 'ArrowLeft') prevTour()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tourActive, nextTour, prevTour, stopTour])

  const rect = useAnchorRect(step?.anchor, tourActive, tourIndex)

  if (!tourActive || !step) return null

  const isLast = tourIndex >= tourSteps.length - 1
  const pad = 8
  const placeAbove = rect ? rect.top > window.innerHeight / 2 : false
  const tooltipWidth = 340

  const tooltipStyle: CSSProperties = rect
    ? {
        top: placeAbove ? rect.top - 14 : rect.top + rect.height + 14,
        left: Math.min(
          Math.max(rect.left, 16),
          Math.max(16, window.innerWidth - tooltipWidth - 16),
        ),
        transform: placeAbove ? 'translateY(-100%)' : undefined,
        width: tooltipWidth,
        maxWidth: 'calc(100vw - 32px)',
      }
    : {}

  const card = (
    <motion.div
      key={tourIndex}
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="pointer-events-auto rounded-xl border border-border bg-card p-5 shadow-paper-lg"
      style={
        rect
          ? { position: 'fixed', ...tooltipStyle }
          : { width: tooltipWidth, maxWidth: 'calc(100vw - 32px)' }
      }
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-ink">
          <Sparkles className="size-3.5" />
          Tur ghidat · {tourIndex + 1}/{tourSteps.length}
        </span>
        <button
          type="button"
          onClick={stopTour}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
          aria-label="Închide turul"
        >
          <X className="size-4" />
        </button>
      </div>
      <h3 className="mt-2 font-display text-lg font-medium">{step.titlu}</h3>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
      <div className="mt-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevTour} disabled={tourIndex === 0}>
          <ArrowLeft className="size-4" />
          Înapoi
        </Button>
        {isLast ? (
          <Button size="sm" onClick={stopTour}>
            Am înțeles
          </Button>
        ) : (
          <Button size="sm" onClick={nextTour}>
            Continuă
            <ArrowRight className="size-4" />
          </Button>
        )}
      </div>
    </motion.div>
  )

  return (
    <div className="fixed inset-0 z-[80]">
      {rect ? (
        // spotlight: a ring around the anchor that dims everything else
        <motion.div
          className="pointer-events-none absolute rounded-xl"
          initial={false}
          animate={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
          }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          style={{
            boxShadow: '0 0 0 9999px rgba(30, 22, 12, 0.55)',
            border: '2px solid var(--amber)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-foreground/50" onClick={stopTour} />
      )}
      <div
        className={
          rect
            ? 'pointer-events-none absolute inset-0'
            : 'pointer-events-none absolute inset-0 flex items-end justify-center pb-24 sm:items-center sm:pb-0'
        }
      >
        {card}
      </div>
    </div>
  )
}

export function Narrator() {
  return (
    <>
      <CaptionBar />
      <TourOverlay />
    </>
  )
}
